import { useMemo } from 'react'
import { isUndefined } from 'lodash'

import {
  ProgressStep,
  ProgressSteps,
  ProgressStepState,
  textChain,
} from '@revolut-internal/ui-kit'

import { AlertingMetricValue, IncidentTimeline, IncidentTimelineType } from 'types'
import { formatDuration, getFormattedDateTime } from 'utils/formatTimeAndDuration'

type QueueHealthIncidentsStepsProps = {
  state: ProgressStepState
  timelines?: IncidentTimeline[]
}

const INCIDENT_TYPE_TO_LABEL = {
  [IncidentTimelineType.IncidentAcknowledged]: 'Acknowledged',
  [IncidentTimelineType.IncidentResolved]: 'Resolved',
  [IncidentTimelineType.IncidentActionCompleted]: 'completed',
  [IncidentTimelineType.IncidentActionSkipped]: 'skipped',
}

const formatMetricValue = (value?: AlertingMetricValue) => {
  if (isUndefined(value)) {
    return undefined
  }

  switch (value.type) {
    case 'PERCENTAGE':
      return `${value.value}%`
    case 'DURATION':
      return formatDuration(value.value)
    case 'COUNT':
    default:
      return value.value
  }
}

const getTimelineTitle = (timeline: IncidentTimeline) => {
  switch (timeline.type) {
    case IncidentTimelineType.ThresholdBreached:
      return timeline.metricLabel
    case IncidentTimelineType.IncidentActionCompleted:
      return timeline.actionLabel
    default:
      return INCIDENT_TYPE_TO_LABEL[timeline.type]
  }
}

export const QueueHealthIncidentsSteps = ({
  state,
  timelines,
}: QueueHealthIncidentsStepsProps) => {
  const timelineWithCompletedActions = useMemo(
    () =>
      timelines?.filter(
        timeline => timeline.type !== IncidentTimelineType.IncidentActionSkipped,
      ) ?? [],
    [timelines],
  )

  if (!timelines) {
    return null
  }

  return (
    <ProgressSteps variant="vertical-compact">
      {timelineWithCompletedActions.map((timeline, index) => {
        const isMetricBreach = timeline.type === IncidentTimelineType.ThresholdBreached

        return (
          <ProgressStep
            key={`${timeline.type}_${timeline.timestamp}_${index}`}
            state={state}
            indicatorColor={isMetricBreach ? 'warning' : 'accent'}
          >
            <ProgressStep.Title>
              {textChain(getTimelineTitle(timeline), formatMetricValue(timeline.value))}
            </ProgressStep.Title>
            <ProgressStep.Description>
              {timeline.actionNotes && (
                <>
                  {timeline.actionNotes}
                  <br />
                </>
              )}

              {textChain(
                getFormattedDateTime(timeline.timestamp),
                timeline.user &&
                  `by ${timeline.user}${
                    timeline.duration ? ` (${formatDuration(timeline.duration)})` : ''
                  }`,
              )}
            </ProgressStep.Description>
          </ProgressStep>
        )
      })}
    </ProgressSteps>
  )
}
